import { useMemo, useRef, useState, useCallback } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

export const COLORS = ["#FF2E88", "#00E5FF", "#7CFF4F", "#FFB800", "#9D5CFF", "#FF6B3D"];
const SPHERE = new THREE.SphereGeometry(1, 28, 28);
const KNOT = new THREE.ConeGeometry(0.12, 0.18, 12);
const STRING = new THREE.CylinderGeometry(0.008, 0.008, 1.1, 4);
const SPIKE = new THREE.ConeGeometry(0.09, 0.28, 8);
const BIT = new THREE.TetrahedronGeometry(0.09);
const SPIKE_DIRS = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, 0, 1], [0, 0, -1], [0.7, 0.7, 0], [-0.7, 0.7, 0], [0.7, -0.6, 0.4], [-0.7, -0.6, -0.4]];

function BalloonBody({ b }) {
  if (b.kind === "bomb") {
    return (
      <group>
        <mesh geometry={SPHERE} scale={b.r}>
          <meshStandardMaterial color="#14141c" emissive="#ff1e3c" emissiveIntensity={0.35} roughness={0.35} metalness={0.6} />
        </mesh>
        {SPIKE_DIRS.map((d, i) => {
          const v = new THREE.Vector3(...d).normalize();
          const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v);
          return <mesh key={i} geometry={SPIKE} position={v.clone().multiplyScalar(b.r * 1.02).toArray()} quaternion={q}>
            <meshStandardMaterial color="#ff2b4a" emissive="#ff2b4a" emissiveIntensity={0.8} />
          </mesh>;
        })}
      </group>
    );
  }
  const gold = b.kind === "gold";
  return (
    <group>
      <mesh geometry={SPHERE} scale={[b.r, b.r * 1.18, b.r]}>
        {gold
          ? <meshStandardMaterial color="#FFC83D" emissive="#FF9F00" emissiveIntensity={0.45} metalness={0.85} roughness={0.18} />
          : <meshPhysicalMaterial color={b.color} roughness={0.14} metalness={0.05} clearcoat={1} clearcoatRoughness={0.08} sheen={0.4} />}
      </mesh>
      <mesh geometry={KNOT} position={[0, -b.r * 1.2, 0]} rotation={[Math.PI, 0, 0]}>
        <meshStandardMaterial color={gold ? "#FFC83D" : b.color} roughness={0.3} />
      </mesh>
      <mesh geometry={STRING} position={[0, -b.r * 1.2 - 0.6, 0]}>
        <meshBasicMaterial color="#ffffff" transparent opacity={0.55} />
      </mesh>
    </group>
  );
}

function Balloon({ b, onPop, onEscape, running }) {
  const g = useRef();
  useFrame((_, dt) => {
    if (!g.current || b.dead) return;
    if (running.current) { b.y += b.speed * dt; b.t += dt; }
    g.current.position.set(b.x + Math.sin(b.t * b.swayF) * b.sway, b.y, b.z);
    g.current.rotation.z = Math.sin(b.t * b.swayF) * 0.14;
    if (b.kind === "bomb") g.current.rotation.y += dt * 1.2;
    if (b.y > b.top) { b.dead = true; onEscape(b); }
  });
  const hit = (e) => {
    e.stopPropagation();
    if (b.dead || !running.current) return;
    b.dead = true;
    onPop(b, g.current.position.clone());
  };
  return (
    <group ref={g} position={[b.x, b.y, b.z]}>
      <BalloonBody b={b} />
      {/* generous invisible hit target so small fingers still land the tap */}
      <mesh geometry={SPHERE} scale={b.r * 1.45} onPointerDown={hit}>
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}

function Burst({ burst, onDone }) {
  const g = useRef();
  const bits = useMemo(() => Array.from({ length: 16 }, () => ({
    v: new THREE.Vector3((Math.random() - 0.5) * 7, (Math.random() - 0.2) * 7, (Math.random() - 0.5) * 4),
    r: new THREE.Vector3(Math.random() * 8, Math.random() * 8, 0),
  })), []);
  const age = useRef(0);
  useFrame((_, dt) => {
    age.current += dt;
    if (!g.current) return;
    g.current.children.forEach((m, i) => {
      const p = bits[i];
      p.v.y -= 9 * dt;
      m.position.addScaledVector(p.v, dt);
      m.rotation.x += p.r.x * dt; m.rotation.y += p.r.y * dt;
      const s = Math.max(0, 1 - age.current / 0.75);
      m.scale.setScalar(s);
    });
    if (age.current > 0.8) onDone(burst.id);
  });
  return (
    <group ref={g} position={burst.pos}>
      {bits.map((_, i) => (
        <mesh key={i} geometry={BIT}>
          <meshStandardMaterial color={i % 3 === 0 ? "#ffffff" : burst.color} emissive={burst.color} emissiveIntensity={0.6} />
        </mesh>
      ))}
    </group>
  );
}

function pickKind(elapsed) {
  const r = Math.random();
  const bombChance = Math.min(0.16, 0.04 + elapsed * 0.002);
  if (r < 0.06) return "gold";
  if (r < 0.06 + bombChance) return "bomb";
  return "normal";
}

// Spawns, moves and pops balloons. Game rules (points, combos) live in the parent page.
export function BalloonField({ running, startedAt, onPop, onEscape }) {
  const { viewport } = useThree();
  const list = useRef([]);
  const [, force] = useState(0);
  const [bursts, setBursts] = useState([]);
  const spawnT = useRef(0);
  const nextId = useRef(1);
  const rerender = useCallback(() => force((n) => n + 1), []);

  useFrame((_, dt) => {
    if (!running.current) return;
    const elapsed = (performance.now() - startedAt.current) / 1000;
    spawnT.current -= dt;
    if (spawnT.current <= 0) {
      spawnT.current = Math.max(0.26, 0.85 - elapsed * 0.011) * (0.7 + Math.random() * 0.6);
      const kind = pickKind(elapsed);
      const k = Math.min(1, Math.max(0.62, viewport.width / 8));
      const r = (kind === "gold" ? 0.42 : 0.5 + Math.random() * 0.22) * k;
      const halfW = Math.max(0.8, viewport.width / 2 - r * 1.6);
      list.current.push({
        id: nextId.current++, kind, r, color: COLORS[Math.floor(Math.random() * COLORS.length)],
        x: (Math.random() * 2 - 1) * halfW, y: -viewport.height / 2 - 1.4, z: -1.2 + Math.random() * 1.4,
        top: viewport.height / 2 + 1.6, t: Math.random() * 6, sway: 0.15 + Math.random() * 0.35, swayF: 0.8 + Math.random() * 1.2,
        speed: (kind === "gold" ? 2.6 : 1.5) + elapsed * 0.045 + Math.random() * 0.9, dead: false,
      });
      rerender();
    }
  });

  const remove = (b) => { list.current = list.current.filter((x) => x.id !== b.id); rerender(); };
  const pop = (b, pos) => {
    remove(b);
    setBursts((xs) => [...xs, { id: b.id, pos: pos.toArray(), color: b.kind === "gold" ? "#FFC83D" : b.kind === "bomb" ? "#ff2b4a" : b.color }]);
    onPop(b);
  };
  const escape = (b) => { remove(b); onEscape(b); };
  const done = (id) => setBursts((xs) => xs.filter((x) => x.id !== id));

  return (
    <>
      {list.current.map((b) => <Balloon key={b.id} b={b} onPop={pop} onEscape={escape} running={running} />)}
      {bursts.map((x) => <Burst key={x.id} burst={x} onDone={done} />)}
    </>
  );
}
